/** Líneas mensuales. Categórico (una línea por entidad), 2px de trazo, marcadores >=8px,
 *  crosshair y tooltip por defecto. Un solo eje, siempre: nunca dos escalas. */
import { useRef, useState } from "react";
import type { Grafico, Idioma, Serie as SerieT } from "../lib/tipos";
import { num } from "../lib/formato";
import { ANCHO_MOVIL, Ejes, Leyenda, MARGEN, Marco, Tooltip, colorSerie, escalaLineal, ticks, useEsMovil } from "./comun";

const ANCHO = 660;
const ALTO = 220;

export function GraficoSerie({ grafico, idioma }: { grafico: Grafico; idioma: Idioma }) {
  const series = (grafico.series ?? []) as SerieT[];
  const [activo, setActivo] = useState<number | null>(null);
  const ref = useRef<SVGSVGElement>(null);
  const movil = useEsMovil();
  // Menos ancho, casi el mismo alto: en un teléfono el gráfico se lee mejor más cuadrado, y así
  // la escala del viewBox sube de 0,48 a ~0,71.
  const anchoV = movil ? ANCHO_MOVIL : ANCHO;
  const altoV = movil ? 210 : ALTO;
  const cuerpo = movil ? 13 : 10;
  const etiquetas = series[0]?.puntos.map((p) => p.x) ?? [];
  const valores = series.flatMap((s) => s.puntos.map((p) => p.y)).filter((v): v is number => v !== null);
  if (!series.length || !valores.length) return null;

  const min = Math.min(...valores, grafico.referencia ?? Infinity);
  const max = Math.max(...valores, grafico.referencia ?? -Infinity);
  const holgura = (max - min) * 0.12 || Math.abs(max) * 0.1 || 1;
  const ty = escalaLineal(min - holgura, max + holgura, altoV - MARGEN.abajo, MARGEN.arriba);
  const paso = (anchoV - MARGEN.izquierda - MARGEN.derecha) / Math.max(etiquetas.length - 1, 1);
  const tx = (i: number) => MARGEN.izquierda + i * paso;

  const desdeRaton = (e: React.MouseEvent) => {
    const caja = ref.current?.getBoundingClientRect();
    if (!caja) return;
    const x = ((e.clientX - caja.left) / caja.width) * anchoV;
    const i = Math.round((x - MARGEN.izquierda) / paso);
    setActivo(i >= 0 && i < etiquetas.length ? i : null);
  };

  return (
    <>
      <Marco titulo={grafico.titulo} idioma={idioma} ancho={anchoV} alto={altoV}>
        <svg ref={ref} viewBox={`0 0 ${anchoV} ${altoV}`} width="100%" height="100%"
             onMouseMove={desdeRaton} onMouseLeave={() => setActivo(null)}>
          <Ejes ancho={anchoV} alto={altoV} tx={(v) => tx(etiquetas.indexOf(v))} ty={ty}
                ticksY={ticks(min - holgura, max + holgura, movil ? 3 : 4)} etiquetasX={etiquetas}
                unidad={grafico.unidad} referencia={grafico.referencia}
                cuerpo={cuerpo} maxEtiquetas={movil ? 4 : 8} />
          {activo !== null && (
            <line x1={tx(activo)} x2={tx(activo)} y1={MARGEN.arriba} y2={altoV - MARGEN.abajo}
                  stroke="var(--borde-fuerte)" strokeWidth="1" />
          )}
          {series.map((s, si) => {
            const color = colorSerie(si);
            const trozos: string[] = [];
            let abierto = false;
            s.puntos.forEach((p, i) => {
              if (p.y === null) { abierto = false; return; }
              trozos.push(`${abierto ? "L" : "M"}${tx(i)},${ty(p.y)}`);
              abierto = true;
            });
            return (
              <g key={s.nombre}>
                <path d={trozos.join(" ")} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                {s.puntos.map((p, i) =>
                  p.y === null ? null : (
                    <circle key={i} cx={tx(i)} cy={ty(p.y)} r={activo === i ? 5 : 3.2} fill={color}
                            stroke="var(--superficie-1)" strokeWidth="2" />
                  ),
                )}
              </g>
            );
          })}
          {activo !== null && (
            <Tooltip x={tx(activo)} y={MARGEN.arriba + 12} ancho={anchoV}
                     lineas={[String(etiquetas[activo]),
                              ...series.map((s) => `${s.nombre}: ${num(s.puntos[activo]?.y ?? null, 2)}${grafico.unidad ? ` ${grafico.unidad}` : ""}`)]} />
          )}
        </svg>
      </Marco>
      <Leyenda items={series.map((s, i) => ({ nombre: s.nombre, color: colorSerie(i) }))} />
    </>
  );
}
